/**
 * HALO - App Sidebar
 * Collapsible sidebar navigation inspired by shadcn's sidebar pattern.
 * Uses react-router-dom NavLink for active state highlighting.
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Activity,
  Users,
  User,
  Settings,
  BrainCircuit,
  FileDown,
  HelpCircle,
  LogOut,
  ChevronDown,
} from "lucide-react";
// import { Avatar } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// ============================================================================
// SIDEBAR CONTEXT
// ============================================================================

interface SidebarContextType {
  collapsed: boolean;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextType>({
  collapsed: false,
  toggle: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);

  return (
    <SidebarContext.Provider value={{ collapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  );
}

// ============================================================================
// NAV DATA
// ============================================================================

interface NavItem {
  title: string;
  path: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  badge?: string;
  disabled?: boolean;
}

const mainNavItems: NavItem[] = [
  { title: "Dashboard", path: "/", icon: LayoutDashboard },
  { title: "Team Pulse", path: "/team-pulse", icon: Activity },
  { title: "Admin", path: "/admin", icon: Users, adminOnly: true },
];

const secondaryNavItems: NavItem[] = [
  {
    title: "AI Analysis",
    path: "/ai-analysis",
    icon: BrainCircuit,
    badge: "Soon",
    disabled: true,
  },
  {
    title: "Export Data",
    path: "/export",
    icon: FileDown,
    adminOnly: true,
  },
];

const bottomNavItems: NavItem[] = [
  { title: "Settings", path: "/settings", icon: Settings },
  { title: "Help", path: "/help", icon: HelpCircle, disabled: true },
];

// ============================================================================
// SIDEBAR COMPONENT
// ============================================================================

interface AppSidebarProps {
  userRole: "admin" | "manager" | "employee";
  userName: string;
  userEmail: string;
  orgName?: string;
  onLogout: () => void;
}

export function AppSidebar({
  userRole,
  userName,
  userEmail,
  orgName,
  onLogout,
}: AppSidebarProps) {
  const { collapsed } = useSidebar();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const renderNavItem = (item: NavItem) => {
    // Filter admin-only items
    if (item.adminOnly && userRole !== "admin") return null;

    const isActive =
      item.path === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(item.path);

    const content = (
      <div
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150",
          isActive
            ? "bg-primary/15 text-primary"
            : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent",
          item.disabled && "opacity-40 cursor-not-allowed",
          collapsed && "justify-center px-2",
        )}
      >
        <item.icon
          className={cn("shrink-0", collapsed ? "w-5 h-5" : "w-5 h-5")}
        />
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{item.title}</span>
            {item.badge && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                {item.badge}
              </span>
            )}
          </>
        )}
      </div>
    );

    if (item.disabled) {
      return collapsed ? (
        <Tooltip
          key={item.path}
          content={`${item.title} (Coming Soon)`}
          side="right"
        >
          {content}
        </Tooltip>
      ) : (
        <div key={item.path}>{content}</div>
      );
    }

    return collapsed ? (
      <Tooltip key={item.path} content={item.title} side="right">
        <NavLink to={item.path} end={item.path === "/"}>
          {content}
        </NavLink>
      </Tooltip>
    ) : (
      <NavLink key={item.path} to={item.path} end={item.path === "/"}>
        {content}
      </NavLink>
    );
  };

  return (
    <motion.aside
      className={cn(
        "flex flex-col h-screen bg-[var(--sidebar-background)] border-r border-[var(--sidebar-border)] sticky top-0 z-30 shrink-0 overflow-hidden",
      )}
      animate={{
        width: collapsed
          ? "var(--sidebar-width-collapsed)"
          : "var(--sidebar-width)",
      }}
      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 h-14 shrink-0 border-b border-[var(--sidebar-border)]">
        <img
          src="/haloai_logo.webp"
          alt="HALO Logo"
          className="w-8 h-8 object-contain shrink-0"
        />
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 min-w-0"
          >
            <p className="text-sm font-semibold text-foreground truncate">
              {orgName || "HALO"}
            </p>
            <p className="text-[11px] text-muted-slate truncate">
              Virtual Office
            </p>
          </motion.div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto custom-scrollbar px-3 py-4 space-y-1">
        {/* Main nav */}
        <div className="space-y-1">{mainNavItems.map(renderNavItem)}</div>

        <Separator className="my-4" />

        {/* Secondary nav */}
        {!collapsed && (
          <p className="text-[11px] text-muted-slate uppercase tracking-widest font-medium px-3 mb-2">
            Tools
          </p>
        )}
        <div className="space-y-1">{secondaryNavItems.map(renderNavItem)}</div>

        {/* Spacer */}
        <div className="flex-1" />
      </nav>

      {/* Bottom nav */}
      <div className="px-3 pb-2 space-y-1">
        {bottomNavItems.map(renderNavItem)}
      </div>

      <Separator />

      {/* User section */}
      <div className="p-3 relative">
        <button
          onClick={() => setShowUserMenu(!showUserMenu)}
          className={cn(
            "w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-surface-elevated transition-colors",
            collapsed && "justify-center",
          )}
        >
          <div className="w-8 h-8 rounded-xl bg-surface-elevated flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-muted-slate" />
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {userName}
                </p>
                <p className="text-[11px] text-muted-slate truncate">
                  {userEmail}
                </p>
              </div>
              <ChevronDown
                className={cn(
                  "w-4 h-4 text-muted-slate transition-transform",
                  showUserMenu && "rotate-180",
                )}
              />
            </>
          )}
        </button>

        {/* User dropdown */}
        <AnimatePresence>
          {showUserMenu && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              className={cn(
                "absolute bottom-full left-3 right-3 mb-2 bg-surface-dark border border-glass-grey rounded-xl shadow-lg overflow-hidden z-50",
                collapsed && "left-full bottom-3 ml-2 w-48",
              )}
            >
              <div className="p-1">
                <div className="px-3 py-2 border-b border-glass-grey mb-1">
                  <p className="text-sm font-medium text-foreground">
                    {userName}
                  </p>
                  <p className="text-xs text-muted-slate">{userEmail}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary mt-1 inline-block">
                    {userRole}
                  </span>
                </div>
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-signal-crimson hover:bg-signal-crimson/10 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Collapse toggle */}
    </motion.aside>
  );
}
